import { KeenIcon } from '@/components';
import { useState, useMemo, useEffect } from 'react';
import { Link, useParams, useNavigate, useLocation } from 'react-router-dom';
import { useGetRegisteredDMOByIdQuery } from '@/services/DMOManagementService';
import { DMOUserDetailsView } from './DMOUserDetailsView';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useDemo1Layout, Demo1LayoutContext } from '@/layouts/demo1';
import { Statuses } from '@/constant';
import { DmoRegistrationStatus } from '@/types/dmo-management.types';
import { usePermissions } from '@/hooks/usePermissions';

const DMOUserDetails = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { layout } = useDemo1Layout() as unknown as Demo1LayoutContext;
  const { hasPermission } = usePermissions();

  const {
    data: dmoData,
    isLoading,
    isError,
  } = useGetRegisteredDMOByIdQuery(id || '', {
    skip: !id,
  });

  const user = dmoData;

  const statusLabel = useMemo(() => {
    if (!user) return '-';
    const status = String(user.status);
    const isActive =
      status === DmoRegistrationStatus.ACTIVE ||
      status === 'Active' ||
      status === String(Statuses.ACTIVE);
    return isActive ? 'Active' : 'Inactive';
  }, [user]);

  const navItems = useMemo(() => {
    return [{ name: 'DMO Details', href: '#', id: 'details' }];
  }, []);

  const urlSearchParams = new URLSearchParams(location.search);
  const tabFromUrl = urlSearchParams.get('tab');

  const [activeTab, setActiveTab] = useState(
    tabFromUrl && navItems.some((item) => item.id === tabFromUrl)
      ? tabFromUrl
      : navItems[0]?.id || 'details'
  );

  // Update URL when tab changes
  useEffect(() => {
    const currentParams = new URLSearchParams(location.search);
    if (currentParams.get('tab') !== activeTab) {
      currentParams.set('tab', activeTab);
      window.history.replaceState(
        null,
        '',
        `${location.pathname}?${currentParams.toString()}`
      );
    }
  }, [activeTab, location.pathname, location.search]);

  // Mobile pager: cycle through tabs one at a time
  const currentIndex = navItems.findIndex((item) => item.id === activeTab);
  const goPrevTab = () => {
    const prevIndex = (currentIndex - 1 + navItems.length) % navItems.length;
    const id = navItems[prevIndex].id;
    setActiveTab(id);
    window.history.pushState(null, '', `?tab=${id}`);
  };
  const goNextTab = () => {
    const nextIndex = (currentIndex + 1) % navItems.length;
    const id = navItems[nextIndex].id;
    setActiveTab(id);
    window.history.pushState(null, '', `?tab=${id}`);
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'details':
        return user ? <DMOUserDetailsView user={user} /> : null;
      default:
        return user ? <DMOUserDetailsView user={user} /> : null;
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-b-4 border-blue-500" />
      </div>
    );
  }

  if (isError || !user) {
    return (
      <div className="flex flex-col items-center justify-center h-64 space-y-4">
        <p className="text-red-600 text-center">
          Failed to load DMO user details.
        </p>
        <button
          type="button"
          className="btn btn-sm btn-primary flex items-center gap-2"
          onClick={() => navigate('/dmo-management')}
        >
          <KeenIcon type="filled" icon="arrow-left" className="text-white" />
          Back to DMO Management
        </button>
      </div>
    );
  }

  return (
    <div className="dmo-details-page w-full">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center gap-4">
          <div className="mt-3">
            <Link
              to="/dmo-management"
              className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900 mb-4"
              aria-label="Back to dmo management"
            >
              <KeenIcon
                icon="black-left"
                className="text-xl"
                aria-hidden="true"
              />
            </Link>
          </div>
          <header
            className={`flex-1 md:border-b border-gray-200 dmo-header ${layout?.options?.sidebar?.collapse ? 'header-collapsed' : ''}`}
          >
            <div className="flex justify-between items-center mb-2">
              <div className="flex flex-col gap-1 flex-1">
                <h1 className="text-2xl font-bold text-gray-900">
                  {user.organisation_name || user.name || 'DMO Details'}
                </h1>
                <div className="flex items-center gap-2">
                  <span
                    className={`badge badge-sm badge-outline ${statusLabel === 'Active' ? 'badge-success contrast:text-green-200' : 'badge-danger contrast:text-red-200'}`}
                  >
                    {statusLabel}
                  </span>
                  <span className="text-xs text-gray-500">{user.email}</span>
                </div>
              </div>
              <div className="flex items-center gap-2.5">
                {hasPermission('dmo_registration_management_update') && (
                  <button
                    type="button"
                    className="btn btn-sm btn-light flex items-center gap-2 mr-2"
                    onClick={() =>
                      navigate(`/dmo-management/register/${user.id}`)
                    }
                  >
                    <KeenIcon
                      type="filled"
                      icon="pencil"
                      className="text-gray-500"
                    />
                    Edit Details
                  </button>
                )}
              </div>
            </div>
            <nav
              role="tablist"
              className="-mb-px flex space-x-6 overflow-x-auto px-1"
              aria-label="Tabs"
              style={{ maxWidth: 'calc(100vw - 200px)' }}
            >
              {navItems.map((item) => (
                <Link
                  key={item.name}
                  to={`?tab=${item.id}`}
                  onClick={(e) => {
                    e.preventDefault();
                    setActiveTab(item.id);
                    // Update URL without navigation
                    window.history.pushState(null, '', `?tab=${item.id}`);
                  }}
                  role="tab"
                  aria-selected={activeTab === item.id}
                  aria-controls={`dmo-tabpanel-${item.id}`}
                  className={`whitespace-nowrap py-4 px-3 border-b-2 font-medium text-sm flex items-center gap-1 flex-shrink-0 min-w-fit focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-inset rounded-sm ${
                    activeTab === item.id
                      ? 'border-blue-500 text-blue-600 contrast:text-blue-200'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 contrast:text-gray-200'
                  }`}
                >
                  {item.name}
                </Link>
              ))}
            </nav>
            {/* Mobile-only pager: hidden by default, shown at 320px via CSS */}
            <div
              className="dmo-mobile-nav hidden items-center justify-between"
              aria-label="Tabs mobile"
            >
              <button
                type="button"
                className="nav-arrow prev"
                onClick={goPrevTab}
                aria-label="Previous section"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <div className="current-tab" aria-current="page">
                {navItems[currentIndex]?.name}
              </div>
              <button
                type="button"
                className="nav-arrow next"
                onClick={goNextTab}
                aria-label="Next section"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </header>
        </div>
        <div
          className="py-8"
          role="tabpanel"
          id={`dmo-tabpanel-${activeTab}`}
          aria-label={`${navItems[currentIndex]?.name || 'Section'} content`}
        >
          {renderContent()}
        </div>
      </div>
    </div>
  );
};

export default DMOUserDetails;
